import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../store/actions';
import { toast } from 'react-toastify';

import './AddNewProduct.css';

import Spinner from '../../containers/UI/Spinner/Spinner';
import useImageUpload from './hooks/useImageUpload';

import Add from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';

// Static country list for origin select
const COUNTRIES = [
  { name: 'Qatar', code: 'QA' },
  { name: 'United Arab Emirates', code: 'AE' },
  { name: 'Saudi Arabia', code: 'SA' },
  { name: 'Kuwait', code: 'KW' },
  { name: 'Bahrain', code: 'BH' },
  { name: 'Oman', code: 'OM' },
  { name: 'Jordan', code: 'JO' },
  { name: 'Egypt', code: 'EG' },
  { name: 'Lebanon', code: 'LB' },
  { name: 'Turkey', code: 'TR' },
  { name: 'India', code: 'IN' },
  { name: 'Pakistan', code: 'PK' },
  { name: 'Bangladesh', code: 'BD' },
  { name: 'Philippines', code: 'PH' },
  { name: 'Thailand', code: 'TH' },
  { name: 'China', code: 'CN' },
  { name: 'Japan', code: 'JP' },
  { name: 'United States', code: 'US' },
  { name: 'United Kingdom', code: 'GB' },
  { name: 'Germany', code: 'DE' },
  { name: 'France', code: 'FR' },
  { name: 'Italy', code: 'IT' },
  { name: 'Spain', code: 'ES' },
  { name: 'Netherlands', code: 'NL' },
  { name: 'Switzerland', code: 'CH' },
  { name: 'Australia', code: 'AU' },
  { name: 'New Zealand', code: 'NZ' },
  { name: 'Canada', code: 'CA' },
  { name: 'Brazil', code: 'BR' },
  { name: 'Mexico', code: 'MX' },
  { name: 'South Korea', code: 'KR' },
  { name: 'Malaysia', code: 'MY' },
  { name: 'Singapore', code: 'SG' },
  { name: 'Indonesia', code: 'ID' },
  { name: 'Vietnam', code: 'VN' },
  { name: 'Sri Lanka', code: 'LK' },
  { name: 'Nepal', code: 'NP' },
];

/**
 * AddNewProduct - Modernized functional component
 * Form for adding a completely new product to the system
 */
const AddNewProduct = ({ onClose }) => {
  // Redux hooks
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const categories = useSelector((state) => state.addItem.categories);
  const brands = useSelector((state) => state.addItem.brands || []);
  const profile = useSelector((state) => state.profile.profileDetails);
  const loading = useSelector((state) => state.addItem.loading);

  // Image upload hook (replaces Redux image state)
  const {
    images,
    isUploading: loadingImg,
    uploadImage,
    removeImage,
    clearImages,
    getImageFiles,
  } = useImageUpload();

  // Form state - aligned with backend Product and ProductMerchant models
  const [formData, setFormData] = useState({
    Name: '',
    Price: 0,
    CategoryId: 0,
    unit: 'pcs',
    Description: '',
    brendId: 0,
    brandName: '',
    origin: '',
    barCode: '',
    isActive: true,
    stockQuantity: 0,
    discount: 0,
    regularDiscountPrice: 0,
    minOrderQty: 1,
  });

  const [autoFillLoading, setAutoFillLoading] = useState(true);

  // Load brands, categories, and autofill data on mount
  useEffect(() => {
    dispatch(actions.addItemStartModal());
    dispatch(actions.getBrands());
    dispatch(actions.getCategories());

    const loadAutofillData = () => {
      try {
        const autoFillData = localStorage.getItem('Dbutler_Supplier_autofill');
        
        if (autoFillData) {
          const parsedData = JSON.parse(autoFillData);
          const dateToday = new Date();
          const dateOfFillData = new Date(parsedData.dateNow);

          // Reset autofill if month has changed
          if (dateToday.getMonth() !== dateOfFillData.getMonth()) {
            localStorage.removeItem('Dbutler_Supplier_autofill');
          } else {
            // Apply autofill data
            setFormData((prev) => ({
              ...prev,
              CategoryId: parsedData.CategoryId || prev.CategoryId,
              unit: parsedData.unit || prev.unit,
              brandName: parsedData.brandName || prev.brandName,
              brendId: parsedData.brendId || prev.brendId,
              origin: parsedData.origin || prev.origin,
            }));
          }
        }
      } catch (error) {
        console.error('Error loading autofill data:', error);
      } finally {
        setAutoFillLoading(false);
      }
    };

    loadAutofillData();
  }, [dispatch]);

  // Generic input change handler
  const handleInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  // Brand select change handler
  const handleBrandChange = useCallback((event) => {
    const selectedId = event.target.value;
    const selectedBrand = brands.find((b) => String(b.id) === String(selectedId));
    setFormData((prev) => ({
      ...prev,
      brendId: selectedId || 0,
      brandName: selectedBrand?.name || '',
    }));
  }, [brands]);

  // Price and discount input handler with validation
  const handlePriceAndDiscountChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      const { Price } = formData;

      if (name === 'Price') {
        if (!value || value.match(/^\d{1,}(\.\d{0,2})?$/)) {
          setFormData((prev) => ({ ...prev, Price: value }));
        }
      } else if (name === 'regularDiscount') {
        if (!value || value.match(/^\d{1,}(\.\d{0,4})?$/)) {
          const priceAfterDisc = (Price - (value * Price) / 100).toFixed(2);
          setFormData((prev) => ({
            ...prev,
            regularDiscountPrice: priceAfterDisc,
            discount: value,
          }));
        }
      } else if (name === 'regularDiscountPrice') {
        if (!value || value.match(/^\d{1,}(\.\d{0,2})?$/)) {
          const discountAfterPrice = (((Price - value) / Price) * 100).toFixed(2);
          setFormData((prev) => ({
            ...prev,
            regularDiscountPrice: value,
            discount: discountAfterPrice,
          }));
        }
      }
    },
    [formData.Price]
  );

  // Image upload handler - uses new hook
  const handleImageUpload = useCallback(
    (event) => {
      const file = event.target.files[0];
      if (file) {
        uploadImage(file);
      }
      // Reset input so same file can be uploaded again
      event.target.value = null;
    },
    [uploadImage]
  );

  // Image delete handler
  const handleImageDelete = useCallback(
    (index) => {
      removeImage(index);
    },
    [removeImage]
  );

  // Validation - only required fields
  const validateForm = useCallback(() => {
    return new Promise((resolve, reject) => {
      const errorArr = [];
      const { Name, unit, CategoryId, origin, discount, Price } = formData;

      if (!Name || Name.trim().length === 0) {
        errorArr.push('Product name is required!');
      }

      if (!unit || unit.trim().length === 0) {
        errorArr.push('Unit is required!');
      }

      if (!CategoryId || CategoryId === 0) {
        errorArr.push('Category is required!');
      }

      if (!origin || origin.trim() === '') {
        errorArr.push('Country of origin is required!');
      }

      if (!images || !Array.isArray(images) || images.length === 0) {
        errorArr.push('Please upload at least one product image!');
      }

      if (!Price || parseFloat(Price) <= 0) {
        errorArr.push('Price must be greater than 0!');
      }

      if (discount && !(parseFloat(discount) <= 99 && parseFloat(discount) >= 0)) {
        errorArr.push('Discount must be between 0 and 99!');
      }

      if (errorArr.length > 0) {
        reject(errorArr);
      } else {
        resolve();
      }
    });
  }, [formData, images]);

  // Submit handler
  const handleSubmit = useCallback(async () => {
    try {
      await validateForm();

      const {
        Name,
        Price,
        CategoryId,
        unit,
        Description,
        brendId,
        brandName,
        barCode,
        origin,
        discount,
      } = formData;

      const productObj = {
        Name,
        name: Name,
        Price: parseFloat(Price),
        price: parseFloat(Price),
        CategoryId: parseInt(CategoryId),
        categoryId: parseInt(CategoryId),
        unit,
        Description,
        description: Description,
        brendId: parseInt(brendId),
        brandId: parseInt(brendId),
        brandName,
        origin,
        originCountry: origin,
        barCode,
        barcode: barCode,
        isActive: true,
        stockQuantity: 0,
        discount: parseFloat(discount) || 0,
        discountPercent: parseFloat(discount) || 0,
        minOrderQty: 1,
        MinOrderQty: 1,
      };

      const imageFiles = getImageFiles();

      localStorage.setItem(
        'Dbutler_Supplier_autofill',
        JSON.stringify({
          CategoryId,
          brendId,
          brandName,
          unit,
          origin,
          dateNow: new Date(),
        })
      );

      await dispatch(actions.AddNewItem(profile.id, productObj, imageFiles));
      
      clearImages();
      
      if (onClose) {
        onClose();
      } else {
        navigate('/inventory');
      }
    } catch (errors) {
      if (Array.isArray(errors)) {
        errors.forEach((error) => {
          toast.error(error, {
            position: toast.POSITION.BOTTOM_CENTER,
          });
        });
      } else {
        toast.error('An error occurred while adding the product', {
          position: toast.POSITION.BOTTOM_CENTER,
        });
      }
    }
  }, [formData, images, profile, dispatch, validateForm, onClose, getImageFiles, clearImages]);

  // Render category options
  const renderCategories = () => {
    if (!categories || !Array.isArray(categories) || categories.length === 0) {
      return <option value={0}>Loading categories...</option>;
    }
    return (
      <>
        <option value={0}>Select a category</option>
        {categories.map((category) => (
          <option key={category.id} value={category.id}>
            {category.name}
          </option>
        ))}
      </>
    );
  };

  // Render brand options
  const renderBrands = () => {
    return (
      <>
        <option value={0}>Select a brand (optional)</option>
        {brands.map((brand) => (
          <option key={brand.id} value={brand.id}>
            {brand.name}
          </option>
        ))}
      </>
    );
  };

  // Render country options
  const renderCountries = () => {
    return (
      <>
        <option value="">Select country of origin</option>
        {COUNTRIES.map((country) => (
          <option key={country.code} value={country.name}>
            {country.name}
          </option>
        ))}
      </>
    );
  };

  // Render images
  const renderImages = () => {
    if (!images || !Array.isArray(images) || images.length === 0) {
      return (
        <>
          <input
            accept="image/*"
            className="inputAvatarBox"
            id="ItemImg"
            type="file"
            onChange={handleImageUpload}
            disabled={loadingImg}
          />
          <label htmlFor="ItemImg">
            <div className="imgTopPlaceholder">
              {loadingImg ? <Spinner /> : <Add color="inherit" fontSize="large" />}
            </div>
          </label>
        </>
      );
    }

    const firstImage = images[0];
    const imagePreview = firstImage.preview || firstImage.url;

    return (
      <>
        <div className="imageRemoveBig">
          <img src={imagePreview} alt="Main Product" />
          <div className="imageRemoveIcon" onClick={() => handleImageDelete(0)}>
            <ClearIcon fontSize="small" color="inherit" />
          </div>
          {firstImage.status === 'error' && (
            <div style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
              Upload failed: {firstImage.error}
            </div>
          )}
        </div>
      </>
    );
  };

  const renderSmallImages = () => {
    if (!images || !Array.isArray(images) || images.length <= 1) return null;

    return images.slice(1).map((imageItem, index) => {
      const imagePreview = imageItem.preview || imageItem.url;
      return (
        <div className="imgBoxListPlaceholder" key={imageItem.id || index + 1}>
          <img src={imagePreview} alt={`Product ${index + 2}`} />
          <div
            className="imageRemoveIconSmall"
            onClick={() => handleImageDelete(index + 1)}
          >
            <ClearIcon fontSize="inherit" color="inherit" />
          </div>
          {imageItem.status === 'error' && (
            <div style={{ color: 'red', fontSize: '10px' }}>
              Failed
            </div>
          )}
        </div>
      );
    });
  };

  const priceAfterDisc = (
    formData.Price -
    (formData.discount * formData.Price) / 100
  ).toFixed(2);

  return (
    <div className="AddNewProductRoot">
      <div className="addNewProductInfo">
        {autoFillLoading ? (
          <Spinner />
        ) : (
          <div className="addNewProductColumn">
            <span className="priceDiscSpan">Product information</span>

            <div className="addNewInputRow">
              <span style={{ width: '130px', textAlign: 'right' }}>Product name:</span>
              <input
                type="text"
                name="Name"
                value={formData.Name}
                onChange={handleInputChange}
              />
            </div>
            <div className="inputRows">
              <div className="addNewInputRow">
                <span>Barcode (8-14 digits):</span>
                <input
                  type="text"
                  name="barCode"
                  value={formData.barCode}
                  onChange={handleInputChange}
                  placeholder="Optional"
                  style={{ width: '150px', marginLeft: '10px' }}
                />
              </div>
              <div className="addNewInputRow">
                <span>Unit:</span>
                <input
                  type="text"
                  name="unit"
                  value={formData.unit}
                  onChange={handleInputChange}
                  placeholder="pcs, kg, liters, etc."
                  style={{ width: '150px', marginLeft: '10px' }}
                />
              </div>
            </div>
            <div className="addNewInputRow">
              <span style={{ width: '130px', textAlign: 'right' }}>Category:</span>
              <select
                name="CategoryId"
                value={formData.CategoryId}
                onChange={handleInputChange}
              >
                {renderCategories()}
              </select>
            </div>
            <div className="addNewInputRow">
              <span style={{ width: '130px', textAlign: 'right' }}>Brand (optional):</span>
              <select
                name="brendId"
                value={formData.brendId}
                onChange={handleBrandChange}
              >
                {renderBrands()}
              </select>
            </div>
            <div className="addNewInputRow">
              <span style={{ width: '130px', textAlign: 'right' }}>
                Country of origin:
              </span>
              <select
                name="origin"
                value={formData.origin}
                onChange={handleInputChange}
              >
                {renderCountries()}
              </select>
            </div>
            <div className="addNewInputRow noAlign">
              <span style={{ width: '130px', textAlign: 'right' }}>Description (optional):</span>
              <textarea
                cols="30"
                rows="10"
                name="Description"
                value={formData.Description}
                onChange={handleInputChange}
                placeholder="Optional product description"
              ></textarea>
            </div>
          </div>
        )}

        <div>
          <div className="addImgBox">
            <div className="imgBoxTop1">{renderImages()}</div>
            <div className="imgBoxList">
              {renderSmallImages()}
              {images.length > 0 && (
                <>
                  <input
                    accept="image/*"
                    className="inputAvatarBox"
                    id="ItemImgAdd"
                    type="file"
                    onChange={handleImageUpload}
                    disabled={loadingImg}
                  />
                  <label htmlFor="ItemImgAdd" className="ItemImg">
                    <div className="imgBoxListAdd">
                      {loadingImg ? <Spinner /> : <Add color="inherit" fontSize="small" />}
                    </div>
                  </label>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="addNewProductPrice">
        <div className="addNewProductPriceColumn">
          <span className="priceDiscSpan">Pricing and discounts</span>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              height: '70%',
              marginTop: '15px',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'column',
              }}
            >
              <div className="priceInputBox">
                <span>Price:</span>
                <div className="priceDiscInput">
                  <input
                    type="text"
                    name="Price"
                    onChange={handlePriceAndDiscountChange}
                    value={formData.Price}
                  />
                  <div>QAR</div>
                </div>
              </div>

              <div className="priceInputBox">
                <div>
                  <span
                    className="descPriceOldLine"
                    style={{ marginLeft: 0, color: '#6E7170', fontSize: 15 }}
                  >
                    QAR {formData.Price}
                  </span>
                  <span
                    className="descPriceOldPerc"
                    style={{ color: '#6E7170', fontSize: 15 }}
                  >
                    ({formData.discount}% off)
                  </span>
                </div>
                <div className="descPriceNew" style={{ fontSize: 26 }}>
                  QAR {priceAfterDisc}
                </div>
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'column',
              }}
            >
              <div className="priceInputBox">
                <span>Regular Discount:</span>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: 320 }}>
                  <div className="priceDiscInput">
                    <input
                      type="text"
                      value={formData.discount}
                      name="regularDiscount"
                      onChange={handlePriceAndDiscountChange}
                      style={{ width: 70 }}
                    />
                    <div>%</div>
                  </div>
                  <div className="priceDiscInput">
                    <input
                      type="text"
                      value={formData.regularDiscountPrice}
                      name="regularDiscountPrice"
                      onChange={handlePriceAndDiscountChange}
                      style={{ width: 95 }}
                    />
                    <div>QAR</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div>
          <div>
            <div className="addNewInfoText">
              <span>Please make sure product information is correct.</span>
              <br />
              <span>
                Pricing and discounts can be edited any time through My Inventory screen.
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                width: '100%',
                marginTop: '10px',
              }}
            >
              <div className="addBatchButton">
                {loading ? (
                  <Spinner />
                ) : (
                  <div
                    style={{
                      height: '100%',
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                    }}
                    onClick={handleSubmit}
                  >
                    Add product
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddNewProduct;
